import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Panel } from "@/components/Bits";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Copy } from "lucide-react";

const cls = "w-full bg-[#1B1B28] border hairline px-3 py-2 text-sm font-mono focus:outline-none focus:border-cyan-400/60";
const btn = "border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 px-3 py-2 text-[11px] font-mono uppercase tracking-widest disabled:opacity-50 transition-colors duration-150";

export default function MfaPanel() {
  const { user, setSession } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [busy, setBusy] = useState(false);
  const enabled = !!user?.mfa_enabled;

  const refreshUser = async () => { const { data } = await api.get("/auth/me"); setSession({ token: localStorage.getItem("cds_token"), user: data }); };

  const begin = async () => { setBusy(true); try { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); } catch (e) { toast.error(e?.response?.data?.detail || "Could not start enrolment"); } finally { setBusy(false); } };
  const enable = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/mfa/enable", { code }); setRecovery(data.recovery_codes); setSetup(null); setCode(""); await refreshUser(); toast.success("Two-factor authentication enabled"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Code rejected"); } finally { setBusy(false); }
  };
  const disable = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/mfa/disable", { code, password }); setCode(""); setPassword(""); await refreshUser(); toast.success("Two-factor authentication disabled"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not disable"); } finally { setBusy(false); }
  };

  return (
    <Panel kicker="Security · Admin MFA" title="Two-factor authentication" right={<span data-testid="mfa-status" className={`font-mono text-[10px] uppercase tracking-widest ${enabled ? "text-emerald-400" : "text-orange-400"}`}>{enabled ? "enabled" : "off"}</span>}>
      <div className="p-4 space-y-3 text-sm">
        {recovery && (
          <div data-testid="mfa-recovery-codes" className="border border-amber-400/40 bg-amber-400/5 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-300 mb-2">Recovery codes — shown once, store them safely</div>
            <div className="grid grid-cols-2 gap-1 font-mono text-xs">{recovery.map((c) => <span key={c}>{c}</span>)}</div>
            <button onClick={() => navigator.clipboard.writeText(recovery.join("\n")).then(() => toast.success("Copied"))} className="mt-2 text-xs text-amber-200 flex items-center gap-1"><Copy size={12} /> Copy all</button>
          </div>
        )}
        {!enabled && !setup && (
          <>
            <p className="text-xs text-zinc-400">Protect this operator account with a time-based code from Google Authenticator, Authy, 1Password or any TOTP app. Required on every sign-in.</p>
            <button data-testid="mfa-begin" onClick={begin} disabled={busy} className={`${btn} w-full flex items-center justify-center gap-2`}><ShieldCheck size={13} /> Set up authenticator</button>
          </>
        )}
        {setup && (
          <form onSubmit={enable} className="space-y-3">
            <div className="flex gap-4 items-start">
              <img data-testid="mfa-qr" src={setup.qr_png} alt="Scan with your authenticator app" className="w-36 h-36 border hairline bg-white" />
              <div className="text-xs text-zinc-400 space-y-2 min-w-0">
                <p>1. Scan the QR code in your authenticator app.</p>
                <p>2. Or enter this key manually:</p>
                <code data-testid="mfa-secret" className="block font-mono text-[11px] text-cyan-300 break-all">{setup.secret}</code>
                <p>3. Enter the 6-digit code it shows to confirm.</p>
              </div>
            </div>
            <input data-testid="mfa-enable-code" value={code} onChange={(e) => setCode(e.target.value)} className={cls} placeholder="123456" required />
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="mfa-enable-submit" disabled={busy} className={btn}>Confirm & enable</button>
              <button type="button" onClick={() => setSetup(null)} className="border hairline text-zinc-400 hover:bg-white/5 px-3 py-2 text-[11px] font-mono uppercase tracking-widest">Cancel</button>
            </div>
          </form>
        )}
        {enabled && (
          <form onSubmit={disable} className="space-y-2">
            <p className="text-xs text-zinc-400">Your account is protected. To turn MFA off, confirm your password and a current code.</p>
            <input data-testid="mfa-disable-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={cls} placeholder="Current password" required />
            <input data-testid="mfa-disable-code" value={code} onChange={(e) => setCode(e.target.value)} className={cls} placeholder="Authenticator code" required />
            <button data-testid="mfa-disable-submit" disabled={busy} className="w-full border border-red-400/50 text-red-300 hover:bg-red-400/10 px-3 py-2 text-[11px] font-mono uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"><ShieldOff size={13} /> Disable MFA</button>
          </form>
        )}
      </div>
    </Panel>
  );
}
